'use client';

import { MouseEvent, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { cn } from '@utils/cn';
import { getI18nLinkHref } from '@services/i18nPaths';
import { MainMenuLink } from '@type/header-types';
import { useAddTaskItems } from '@components/layout/addTask';
import { MainNavbarLink } from './MainNavbarLink';

interface AddJobMenuProps {
  link: MainMenuLink;
  locale?: Locale;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
}

/**
 * "Add job" in the navbar, with a hover menu offering the two zmeu task
 * entry points alongside it.
 *
 * The trigger stays an ordinary link: hovering reveals the menu, clicking still
 * goes to the add-job form exactly as before. That is why this is not a
 * Headless UI Popover — a Popover button opens on click and would swallow the
 * navigation, which would quietly break the most-used action in the navbar to
 * add a secondary one.
 *
 * Desktop only, and that is enough: the header's link row is hidden below lg,
 * and mobile gets the same choices from the bottom bar's add-job cell, which
 * opens on tap because hover does not exist there.
 */
export const AddJobMenu = ({ link, locale, onClick }: AddJobMenuProps) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const items = useAddTaskItems();

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      // Keyboard users never fire mouseenter, so the same menu opens on focus
      // anywhere inside and closes when focus leaves.
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <MainNavbarLink
        type={link.type}
        route={link.route}
        label={link.label}
        icon={link.icon}
        locale={locale}
        onClick={onClick}
        dataTestId={link.dataTestId}
      />

      <div
        className={cn(
          'absolute right-0 top-full z-50 min-w-max overflow-hidden rounded-md bg-surface shadow-lg ring-1 ring-ink/10',
          open ? 'block' : 'hidden',
        )}
        role="menu"
        aria-hidden={!open}
      >
        {/* The add-job route, listed alongside the zmeu entries so the menu reads
            as a complete set of choices rather than "the other two things". */}
        <a
          href={getI18nLinkHref(link.route, locale as Locale)}
          role="menuitem"
          data-test-id="navbar-add-job-menu-item"
          className="flex items-center whitespace-nowrap px-4 py-2.5 text-sm font-semibold text-ink hover:bg-surface-50 hover:text-primary"
        >
          {t('label.job')}
        </a>

        {/* One row per way into zmeu — label and icon are the same link, so the
            wording is as clickable as the icon next to it. */}
        {items.map(({
          href, icon: Icon, ariaLabel, dataTestId, external,
        }) => (
          <a
            key={dataTestId}
            href={href}
            role="menuitem"
            title={ariaLabel}
            data-test-id={dataTestId}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className="flex items-center justify-between gap-4 whitespace-nowrap border-t border-ink/5 px-4 py-2.5 text-sm font-semibold text-ink hover:bg-surface-50 hover:text-primary"
          >
            {t('label.task')}
            <Icon className="size-5 shrink-0 text-green-600" />
          </a>
        ))}
      </div>
    </div>
  );
};
